import { USE_SHADOWS } from "@/helpers/constants";
import * as THREE from "three";
import { MeshLine, MeshLineMaterial } from "three.meshline";
import { CSS2DObject } from "three/examples/jsm/renderers/CSS2DRenderer.js";
import gsap from "gsap";
import { metersToFeet, calculateDistance } from "./surveyingUtils";

export const setCastShadow = (parent, castShadow = true) => {
  if (!USE_SHADOWS) {
    return;
  }
  parent.traverse(function (object) {
    if (object.isMesh) {
      object.castShadow = castShadow;
    }
  });
};

export const setReceiveShadow = (parent, receiveShadow = true) => {
  if (!USE_SHADOWS) {
    return;
  }
  parent.traverse(function (object) {
    if (object.isMesh) {
      object.receiveShadow = receiveShadow;
    }
  });
};

/**
 * Sets the shadow properties of a light object
 * TODO: Needs optimization review - consider baking shadows into models
 * @param {*} light THREE directional light object
 */
export const configureLightShadowSettings = (light) => {
  if (!USE_SHADOWS) {
    return;
  }

  // Tighter shadow camera frustum for better resolution in the area of interest
  const shadowCameraSize = 25; // Reduced for higher detail

  light.castShadow = true;

  // Configure shadow camera frustum
  light.shadow.camera.top = shadowCameraSize;
  light.shadow.camera.bottom = -shadowCameraSize;
  light.shadow.camera.left = shadowCameraSize;
  light.shadow.camera.right = -shadowCameraSize;

  // Optimize shadow map resolution based on device capabilities
  const pixelRatio = window.devicePixelRatio || 1;
  const maxTextureSize = Math.min(4096, light.shadow.mapSize.maxWidth || 4096);

  // Calculate optimal shadow map size (power of 2) based on screen size and device
  const targetSize = Math.min(
    maxTextureSize,
    Math.pow(2, Math.floor(Math.log2(window.innerWidth * pixelRatio * 2)))
  );

  light.shadow.mapSize.width = targetSize;
  light.shadow.mapSize.height = targetSize;

  // Fine-tune shadow bias based on scene scale
  light.shadow.normalBias = 0.001; // Helps reduce shadow acne on small objects
  light.shadow.bias = -0.0001;

  // Enable VSM shadows for better quality at edges
  light.shadow.type = THREE.PCFSoftShadowMap;
};

export const configureRendererShadowSettings = (renderer) => {
  if (!USE_SHADOWS) {
    return;
  }
  renderer.shadowMap.enabled = true;

  // Hides the parts of the stakes that below ground by clipping at the floor height
  const clipPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  renderer.clippingPlanes = [clipPlane];
  renderer.localClippingEnabled = true;
};

/**
 * Creates and animates a thick, dashed line that grows from startPoint to endPoint over 2 seconds using MeshLine.
 * The THREE.Line class is not used here because it does not allow for thickness configuration.
 * @todo Look into using Line2 for this mesh, rather than MeshLine. This may be a more performant solution that still allows for thickness configuration.
 * @param {THREE.Vector3} startPoint - The starting point of the line
 * @param {THREE.Vector3} endPoint - The ending point of the line
 * @param {THREE.Scene} scene - The scene to add the line to
 */
export const createMeasurementLine = (startPoint, endPoint, scene) => {
  if (!startPoint || !endPoint) return;

  // Define the line points as an array of Vec3
  const points = [];
  const divisions = 100; // Number of segments for smoothness
  for (let i = 0; i <= divisions; i++) {
    const t = i / divisions;
    const x = THREE.MathUtils.lerp(startPoint.x, endPoint.x, t);
    const y = THREE.MathUtils.lerp(startPoint.y, endPoint.y, t);
    const z = THREE.MathUtils.lerp(startPoint.z, endPoint.z, t);
    points.push(new THREE.Vector3(x, y, z));
  }

  // Initialize MeshLine and set points
  const meshLine = new MeshLine();
  meshLine.setPoints([points[0]]);

  // Create MeshLineMaterial with desired properties
  const material = new MeshLineMaterial({
    color: new THREE.Color(0xffffff),
    lineWidth: 0.05, // Adjust for thickness
    dashArray: 0.1, // Total dash length
    dashRatio: 0.5, // Fraction of dash that's solid
    dashOffset: 1, // Start offset
    transparent: true,
    opacity: 1
  });

  // Create Mesh and add to the scene
  const mesh = new THREE.Mesh(meshLine.geometry, material);
  mesh.visible = false; // Start hidden for animation
  scene.add(mesh);

  const measurementLine = { mesh, material };

  const distance = calculateDistance(startPoint, endPoint);
  // animation should be 1 second per 6 feet
  const animationDuration = metersToFeet(distance) / 6;

  // Animate the dash offset to create an unfurling effect
  const indexContainer = { index: 0 };
  gsap.to(indexContainer, {
    index: divisions,
    duration: animationDuration,
    ease: "none",
    onStart: () => {
      measurementLine.mesh.visible = true;
    },
    onUpdate: () => {
      const index = Math.floor(indexContainer.index);
      if (index <= meshLine.points.length) return;
      const newPoints = [...meshLine.points, points[index]];
      meshLine.setPoints(newPoints);
    },
    onComplete: () => {
      // Final adjustments if needed
      meshLine.setPoints(points);

      // Place the label at the center of the mesh
      // Ensure the mesh's geometry bounding box is calculated
      measurementLine.mesh.geometry.computeBoundingBox();
      // Get the center of the bounding box
      const center = new THREE.Vector3();
      measurementLine.mesh.geometry.boundingBox.getCenter(center);
      // If the mesh has been transformed (translated, rotated, scaled), apply those transformations
      measurementLine.mesh.localToWorld(center);
      // Calculate the distance between the start and end points

      const label = measurementLine.mesh.children.find(
        (child) => child instanceof CSS2DObject
      );
      if (label) {
        label.element.textContent = `${metersToFeet(distance).toFixed(2)}ft`;
        // Position the label at the center of the mesh
        label.position.copy(center);

        // Optionally, adjust the label's position slightly below the mesh for better visibility
        label.position.y -= 0.1;
        // make label visible
        label.element.style.visibility = "visible";
      }
    }
  });
  return measurementLine;
};

export const createLabelDiv = (
  text,
  color = "white",
  backgroundColor = "rgba(0, 0, 0, 0.6)",
  onTop = false
) => {
  const labelDiv = document.createElement("div");
  labelDiv.classList.add("stake-label");
  labelDiv.textContent = text;
  // Apply inline styles
  labelDiv.style.backgroundColor = backgroundColor;
  labelDiv.style.color = color;
  if (onTop) {
    labelDiv.classList.add("on-top");
  }
  return labelDiv;
};
